import {
  configure,
  hitsPerPage,
  index,
  sortBy,
} from "instantsearch.js/es/widgets/index.js";

import {
  chamfrBlogsHits,
  chamfrClearAllFilters,
  chamfrPagination,
  chamfrProductsHits,
  chamfrProductsSortBy,
  chamfrRefinementList,
  chamfrResultsHeader,
  chamfrSearchBox,
  chamfrSelectedFilters,
  chamfrStats,
  chamfrSuppliersHits,
} from "./widgets/index.js";

import {
  chamfrActiveTabMiddleware,
  chamfrColumnConfigMiddleware,
  chamfrLoadingSkeletonMiddleware,
  chamfrNothingFoundMiddleware,
  chamfrOutOfStockMiddleware,
  chamfrSearchForQueryMiddleware,
} from "./middleware/index.js";
import { createNewSearchInstance } from "./components/search-instance.js";
import { facets, filterAssignments } from "./facets.js";
import {
  createCustomLabels,
  createTableColumns,
  renderFilters,
} from "./components/index.js";

export const initializeSearch = (searchElement) => {
  const search = createNewSearchInstance();
  search.use(chamfrActiveTabMiddleware);
  search.use(chamfrSearchForQueryMiddleware);
  search.use(chamfrOutOfStockMiddleware);
  search.use(chamfrNothingFoundMiddleware);
  search.use(chamfrLoadingSkeletonMiddleware);
  search.use(chamfrColumnConfigMiddleware());

  const products = index({
    indexName: algolia.product_index_name,
    indexId: "products",
  });

  const columns = [...filterAssignments["SERP"].displayHits].sort(
    (a, b) => a.orderColumns - b.orderColumns
  );

  const filters = [...filterAssignments["SERP"].displayFacets].sort(
    (a, b) => a.orderFacets - b.orderFacets
  );

  const widgets = [
    configure({ getRankingInfo: true, clickAnalytics: true }),
    chamfrStats({
      container: searchElement.querySelector(
        "#chamfr-search-products-tab-found"
      ),
    }),
    chamfrResultsHeader({
      container: searchElement.querySelector(
        ".chamfr-search-tab-products .results-header"
      ),
    }),
    hitsPerPage({
      container: searchElement.querySelector(
        ".chamfr-search-tab-products .display-per-page span"
      ),
      items: [
        { label: "50 Items", value: 50, default: true },
        { label: "100 Items", value: 100 },
        { label: "250 Items", value: 250 },
      ],
    }),
    chamfrPagination({
      container: searchElement.querySelector(
        ".chamfr-search-tab-products .chamfr-pagination"
      ),
    }),
    chamfrClearAllFilters({
      container: searchElement.querySelector(
        ".chamfr-search-tab-products .selected-filters .clear-all-filters"
      ),
    }),
  ];

  const filtersContainer = searchElement.querySelector(
    ".search-results-leftside"
  );

  const customLabels = createCustomLabels(filters);
  const tableColumns = createTableColumns(columns);

  widgets.push(...renderFilters(filters, filtersContainer, searchElement));

  widgets.push(
    chamfrSelectedFilters({
      container: searchElement.querySelector(".selected-filters ul"),
      customLabels: Object.fromEntries(customLabels),
    })
  );

  widgets.push(
    chamfrProductsSortBy({
      container: searchElement.querySelector("table.products .header"),
      items: [],
      defaultIndex: algolia.product_index_name,
      columns: tableColumns,
    })
  );

  widgets.push(
    chamfrProductsHits({
      container: searchElement.querySelector("table.products .list"),
      columns: tableColumns,
    })
  );

  products.addWidgets(widgets);

  const suppliers = index({
    indexName: algolia.supplier_index_name,
    indexId: "suppliers",
  });
  suppliers.addWidgets([
    configure({ getRankingInfo: true, clickAnalytics: true }),
    chamfrStats({
      container: searchElement.querySelector(
        "#chamfr-search-suppliers-tab-found"
      ),
    }),
    chamfrRefinementList({
      container: searchElement.querySelector(
        ".chamfr-search-tab-suppliers .chamfr-suppliers-markets"
      ),
      attribute: "supplier_market",
      limit: 10,
      showMore: true,
      showMoreLimit: 100,
    }),
    chamfrRefinementList({
      container: searchElement.querySelector(
        ".chamfr-search-tab-suppliers .chamfr-suppliers-categories"
      ),
      attribute: "supplier_category",
      limit: 10,
      showMore: true,
      showMoreLimit: 100,
    }),
    chamfrRefinementList({
      container: searchElement.querySelector(
        ".chamfr-search-tab-suppliers .chamfr-suppliers-service-type"
      ),
      attribute: "service_type",
      limit: 10,
      showMore: true,
      showMoreLimit: 100,
    }),
    chamfrSuppliersHits({
      container: searchElement.querySelector(
        ".chamfr-search-tab-suppliers .chamfr-suppliers-companies"
      ),
    }),
    chamfrResultsHeader({
      container: searchElement.querySelector(
        ".chamfr-search-tab-suppliers .results-header"
      ),
    }),
    hitsPerPage({
      container: searchElement.querySelector(
        ".chamfr-search-tab-suppliers .display-per-page span"
      ),
      items: [
        { label: "30 Items", value: 30, default: true },
        { label: "100 Items", value: 100 },
        { label: "All Items", value: 1000 },
      ],
    }),
    sortBy({
      container: searchElement.querySelector(
        ".chamfr-search-tab-suppliers .sort-by span"
      ),
      items: [
        { label: "Relevance", value: algolia.supplier_index_name },
        { label: "A-Z", value: `${ algolia.supplier_index_name }_az` },
        { label: "Z-A", value: `${ algolia.supplier_index_name }_za` },
      ],
    }),
    chamfrPagination({
      container: searchElement.querySelector(
        ".chamfr-search-tab-suppliers .chamfr-pagination"
      ),
    }),
    chamfrSelectedFilters({
      container: searchElement.querySelector(
        ".chamfr-search-tab-suppliers .selected-filters ul"
      ),
    }),
    chamfrClearAllFilters({
      container: searchElement.querySelector(
        ".chamfr-search-tab-suppliers .selected-filters .clear-all-filters"
      ),
    }),
  ]);

  const blog = index({
    indexName: algolia.post_index_name,
    indexId: "posts",
  });
  blog.addWidgets([
    configure({ getRankingInfo: true, clickAnalytics: true }),
    chamfrStats({
      container: searchElement.querySelector("#chamfr-search-blogs-tab-found"),
    }),
    chamfrBlogsHits({
      container: searchElement.querySelector(
        ".chamfr-search-tab-blogs .search-results-list"
      ),
    }),
    chamfrRefinementList({
      container: searchElement.querySelector(
        ".chamfr-search-tab-blogs .chamfr-blog-categories"
      ),
      attribute: "post_category",
      limit: 10,
      showMore: true,
      showMoreLimit: 100,
    }),
    chamfrResultsHeader({
      container: searchElement.querySelector(
        ".chamfr-search-tab-blogs .results-header"
      ),
    }),
    hitsPerPage({
      container: searchElement.querySelector(
        ".chamfr-search-tab-blogs .display-per-page span"
      ),
      items: [
        { label: "10 Items", value: 10, default: true },
        { label: "30 Items", value: 30 },
        { label: "All Items", value: 1000 },
      ],
    }),
    sortBy({
      container: searchElement.querySelector(
        ".chamfr-search-tab-blogs .sort-by span"
      ),
      items: [
        { label: "Relevance", value: algolia.post_index_name },
        { label: "Most Recent", value: `${ algolia.post_index_name }_date_desc` },
        { label: "Oldest", value: `${ algolia.post_index_name }_date_asc` },
      ],
    }),
    chamfrPagination({
      container: searchElement.querySelector(
        ".chamfr-search-tab-blogs .chamfr-pagination"
      ),
    }),
    chamfrSelectedFilters({
      container: searchElement.querySelector(
        ".chamfr-search-tab-blogs .selected-filters ul"
      ),
    }),
    chamfrClearAllFilters({
      container: searchElement.querySelector(
        ".chamfr-search-tab-blogs .selected-filters .clear-all-filters"
      ),
    }),
  ]);

  search.addWidgets([
    chamfrSearchBox({
      container: document.querySelector("#chamfr-search-bar .search-form"),
      label: "Search",
    }),
    products,
    suppliers,
    blog,
  ]);

  search.start();
};
